import { useRef } from 'preact/hooks';
import useAppDispatch from '../../hooks/useAppDispatch';
import Button from '../button/Button';
import ProductImage from '../product/product-image/ProductImage';
import useAppSelector from '../../hooks/useAppSelector';
import './SearchByImageSelector.less';
import Loader from '../loader/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import errorImageSrc from '../../img/image-upload-error.svg';
import MainFeatureError from './common/MainFeatureError';
import Chip from '../chip/Chip';
import ImageUpload from './ImageUpload';

const SearchByImageSelector = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { mainFeatureItem, sidebarExpanded } = useAppSelector(
        state => state.featureState
    );

    if (mainFeatureItem?.featureType !== 'product-search-by-image') {
        return null;
    }

    const { loadingStatus } = mainFeatureItem;

    if (loadingStatus === 'loading') {
        return (
            <Loader
                title={t('PRODUCT_SEARCH_BY_IMAGE_LOADING_TITLE')}
                subtitle={t('PRODUCT_SEARCH_BY_IMAGE_LOADING_SUBTITLE')}
            />
        );
    }

    if (loadingStatus === 'error') {
        return (
            <>
                <MainFeatureError
                    imageSrc={errorImageSrc}
                    title={t('PRODUCT_SEARCH_BY_IMAGE_UPLOAD_ERROR')}
                    subtitle={
                        mainFeatureItem.message ||
                        t('PRODUCT_SEARCH_BY_IMAGE_UPLOAD_ERROR_SUBTITLE')
                    }
                    retryButtonLabel={t(
                        'PRODUCT_SEARCH_BY_IMAGE_UPLOAD_ANOTHER'
                    )}
                    onRetryClick={() => fileInputRef.current?.click()}
                />
                <ImageUpload ref={fileInputRef} />
            </>
        );
    }

    if (loadingStatus !== 'success') {
        return null;
    }

    const { selectedBoundingBox } = mainFeatureItem;

    const SelectedCategory = () => (
        <div className="bri-SearchByImageSelector-result bri-DisplayFont">
            {selectedBoundingBox && (
                <>
                    {t('PRODUCT_SEARCH_BY_IMAGE_SELECTED_RESULT') + ' '}
                    <Chip variant="primary-50">
                        {selectedBoundingBox.categoryTranslatedValue ??
                            selectedBoundingBox.categoryValue}
                    </Chip>
                </>
            )}
            {!selectedBoundingBox && t('PRODUCT_SEARCH_BY_IMAGE_FULL_IMAGE')}
        </div>
    );

    return (
        <div className="bri-SearchByImageSelector">
            <ProductImage
                image={{
                    url: mainFeatureItem.imageDataUrl,
                    categoryBoundingBoxes: mainFeatureItem.boundingBoxes,
                    width: mainFeatureItem.imageWidth,
                    height: mainFeatureItem.imageHeight
                }}
                isVisible={true}
                defaultWidth={100}
                showBoundingBoxes
                selectedBoundingBox={selectedBoundingBox}
                onChangeSelectedBoundingBox={selectedBoundingBox =>
                    dispatch.featureState.updateMainFeatureItem({
                        ...mainFeatureItem,
                        selectedBoundingBox
                    })
                }
            />
            <div className="bri-SearchByImageSelector-text">
                {mainFeatureItem.boundingBoxes.length === 0 && (
                    <div className="bri-SearchByImageSelector-result bri-TitleBoldFont">
                        {t('PRODUCT_SEARCH_BY_IMAGE_NO_RESULTS')}
                    </div>
                )}
                {mainFeatureItem.boundingBoxes.length > 0 &&
                    sidebarExpanded && <SelectedCategory />}
            </div>
            <ImageUpload ref={fileInputRef} />
            <div
                className={`bri-SearchByImageSelector-footer ${sidebarExpanded ? '' : 'bri-visible'}`}
            >
                <div className="bri-SearchByImageSelector-footer-column">
                    <SelectedCategory />
                </div>
                <div className="bri-SearchByImageSelector-footer-buttons">
                    <Button
                        variant="primary"
                        startIconId="similar"
                        onClick={() =>
                            dispatch.featureState.startFeature({
                                featureType: 'product-search-similar-by-image',
                                imageId: mainFeatureItem.imageId,
                                imageUrl: mainFeatureItem.imageUrl,
                                imageDataUrl: mainFeatureItem.imageDataUrl,
                                imageWidth: mainFeatureItem.imageWidth,
                                imageHeight: mainFeatureItem.imageHeight,
                                boundingBoxes: mainFeatureItem.boundingBoxes,
                                selectedBoundingBox:
                                    mainFeatureItem.selectedBoundingBox
                            })
                        }
                    >
                        {t('PRODUCT_SEARCH_BY_IMAGE_SIMILAR')}
                    </Button>
                    <Button
                        variant="primary"
                        startIconId="join"
                        onClick={() =>
                            dispatch.featureState.startFeature({
                                featureType:
                                    'product-search-combinations-by-image',
                                imageId: mainFeatureItem.imageId,
                                imageUrl: mainFeatureItem.imageUrl,
                                imageDataUrl: mainFeatureItem.imageDataUrl,
                                imageWidth: mainFeatureItem.imageWidth,
                                imageHeight: mainFeatureItem.imageHeight,
                                boundingBoxes: mainFeatureItem.boundingBoxes,
                                selectedBoundingBox:
                                    mainFeatureItem.selectedBoundingBox
                            })
                        }
                    >
                        {t('PRODUCT_SEARCH_BY_IMAGE_COMBINATIONS')}
                    </Button>
                </div>
                <div className="bri-SearchByImageSelector-footer-column bri-right-align">
                    <Button
                        variant="secondary"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {t('PRODUCT_SEARCH_BY_IMAGE_UPLOAD_ANOTHER')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SearchByImageSelector;
